import { styled } from "@linaria/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { DialogTitle } from "@radix-ui/react-dialog"
import { observable } from "mobx"
import { useMemo, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"


import { AgentIterationStyles } from "@/components/AgentIteration/AgentIterationStyles.ts"
import { DialogContent, DialogOverlay } from "@/components/Dialog/Dialog.tsx"
import { getDialogOnInteractOutsideHandler, getDialogOnOpenAutoFocusHandler } from "@/components/Dialog/utils.ts"
import { DiffContainerMarker } from "@/components/Diff/Diff.tsx"
import { DiffFileComponent } from "@/components/DiffFile/DiffFile.tsx"
import { DiffFileHeaderStyles } from "@/components/DiffFile/DiffFileHeaderStyles.ts"
import { EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { MarkdownWrapper } from "@/components/Markdown/MarkdownWrapper.tsx"
import { getLineGutterWidth } from "@/lib/diff/codeRenderingCommon.ts"
import { ellipsis } from "@/styles/mixins.ts"

import type { GitCommitChangeMin } from "@/components/DiffFile/DiffFileModel.ts"
import type { UIToolCallObservation } from "@/lib/client/generated/TypeDefinitions.ts"

export const ReasoningResult_DiffDialog = (props: { content: UIToolCallObservation.Diff, filename: string }) => {
  const filename = props.filename
  const gitDiffMin: GitCommitChangeMin = {
    old: props.content.oldPath ? { path: props.content.oldPath } : null,
    new: props.content.newPath ? { path: props.content.newPath } : null
  }

  const contentRef = useRef<HTMLDivElement>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const gutterWidth = useMemo(() => observable.box(getLineGutterWidth(props.content.diff)), [props.content.diff])

  useHotkeys("Escape", (e) => {
    setDialogOpen(false)
    e.stopPropagation()
  })

  return (
    <DialogPrimitive.Root open={dialogOpen}>
      <DialogPrimitive.Trigger asChild>
        <Filename onClick={() => setDialogOpen(true)}>
          <EllipsisContainer>
            <MarkdownWrapper
              text={`_\`${filename}\`_`}
              inlineOnly
              className={AgentIterationStyles.secondLevelMarkdown} />
          </EllipsisContainer>
        </Filename>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogOverlay onClick={() => setDialogOpen(false)} />
        <StyledDialogContent
          ref={contentRef}
          aria-describedby={undefined}
          onClick={(e) => e.stopPropagation()}
          onOpenAutoFocus={getDialogOnOpenAutoFocusHandler(contentRef)}
          onCloseAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={getDialogOnInteractOutsideHandler()}
        >
          <StyledDialogTitle>Changes in {filename}</StyledDialogTitle>
          <StyledDiffFileComponent
            file={gitDiffMin}
            diff={props.content.diff}
            gutterWidth={gutterWidth}
            panelWidth={() => "720px"}
            isCollapsible={false}
            close={() => setDialogOpen(false)}
          />
        </StyledDialogContent>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

const StyledDialogTitle = styled(DialogTitle)`
  display: none;
`

const StyledDialogContent = styled(DialogContent)`
  overflow: hidden;

  padding: 0;

  .${DiffContainerMarker} {
    width: auto;
  }
`

const StyledDiffFileComponent = styled(DiffFileComponent)`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  max-height: 90vh;

  ${DiffFileHeaderStyles.Container} {
    padding: 8px 24px;
  }

  ${DiffFileHeaderStyles.CloseButton} {
    top: 16px;
    right: 16px;
  }
`

const Filename = styled.span`
  ${ellipsis};

  cursor: pointer;

  &:hover {
    color: var(--text-color);
  }
`